// src/components/routes/Sign/ResetPassword/ResetPassword.js
import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthShell from '../AuthShell/AuthShell';
import './ResetPassword.css';

const ResetPassword = () => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [visiblePasswords, setVisiblePasswords] = useState({
        newPassword: false,
        confirmPassword: false,
    });
    const navigate = useNavigate();
    const { token } = useParams();
    const passwordChecks = {
        length: newPassword.length >= 8,
        uppercase: /[A-Z]/.test(newPassword),
        lowercase: /[a-z]/.test(newPassword),
        number: /\d/.test(newPassword)
    };

    const togglePasswordVisibility = (field) => {
        setVisiblePasswords((current) => ({
            ...current,
            [field]: !current[field],
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Password validation
        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters long');
            return;
        }

        if (!/[A-Z]/.test(newPassword)) {
            setError('Password must contain at least one uppercase letter');
            return;
        }

        if (!/[a-z]/.test(newPassword)) {
            setError('Password must contain at least one lowercase letter');
            return;
        }

        if (!/\d/.test(newPassword)) {
            setError('Password must contain at least one number');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setIsLoading(true);
        setError('');
        setMessage('');

        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const response = await axios.post(`${apiUrl}/reset-password/${token}`, { 
                newPassword 
            });
            
            if (response.data.success) {
                setMessage('Password reset successful!');
                
                // Redirect to login after 2 seconds
                setTimeout(() => {
                    navigate('/login');
                }, 2000);
            } else {
                setError('Failed to reset password. Please try again.');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AuthShell
            eyebrow="Secure your account"
            title="Create a new password"
            description="Choose a strong password you haven’t used before. You’ll be redirected to sign in when it’s updated."
            asideTitle="Fresh credentials. Same trusted workspace."
            asideDescription="Your new password is protected in transit and your active customer data remains safe and unchanged."
        >
            {message && (
                <div className="auth-alert auth-alert-success" role="status">
                    <i className="fas fa-check-circle" aria-hidden="true" />
                    <span>{message} Redirecting you to sign in…</span>
                </div>
            )}
            {error && (
                <div className="auth-alert auth-alert-error" role="alert">
                    <i className="fas fa-exclamation-circle" aria-hidden="true" />
                    <span>{error}</span>
                </div>
            )}
            <form className="auth-form" onSubmit={handleSubmit}>
                <div className="auth-field">
                    <label htmlFor="new-password">New password</label>
                    <div className="auth-input-wrap">
                        <i className="fas fa-key auth-input-icon" aria-hidden="true" />
                            <input
                                id="new-password"
                                type={visiblePasswords.newPassword ? "text" : "password"}
                                value={newPassword}
                                onChange={(e) => {
                                    setNewPassword(e.target.value);
                                    setError('');
                                }}
                                placeholder="Create a strong password"
                                required
                                disabled={isLoading}
                                minLength="8"
                            />
                            <button
                                type="button"
                                className="auth-password-toggle"
                                onClick={() => togglePasswordVisibility('newPassword')}
                                disabled={isLoading}
                                aria-label={visiblePasswords.newPassword ? "Hide new password" : "Show new password"}
                                title={visiblePasswords.newPassword ? "Hide new password" : "Show new password"}
                            >
                                <i className={`fas fa-eye${visiblePasswords.newPassword ? '-slash' : ''}`} />
                            </button>
                    </div>
                </div>

                <div className="password-strength" aria-label="Password requirements">
                    <span className={passwordChecks.length ? 'met' : ''}><i className="fas fa-check" /> 8+ characters</span>
                    <span className={passwordChecks.uppercase ? 'met' : ''}><i className="fas fa-check" /> Uppercase</span>
                    <span className={passwordChecks.lowercase ? 'met' : ''}><i className="fas fa-check" /> Lowercase</span>
                    <span className={passwordChecks.number ? 'met' : ''}><i className="fas fa-check" /> Number</span>
                </div>

                <div className="auth-field">
                    <label htmlFor="confirm-password">Confirm password</label>
                    <div className="auth-input-wrap">
                        <i className="fas fa-shield-alt auth-input-icon" aria-hidden="true" />
                            <input
                                id="confirm-password"
                                type={visiblePasswords.confirmPassword ? "text" : "password"}
                                value={confirmPassword}
                                onChange={(e) => {
                                    setConfirmPassword(e.target.value);
                                    setError('');
                                }}
                                placeholder="Repeat your new password"
                                required
                                disabled={isLoading}
                                minLength="8"
                            />
                            <button
                                type="button"
                                className="auth-password-toggle"
                                onClick={() => togglePasswordVisibility('confirmPassword')}
                                disabled={isLoading}
                                aria-label={visiblePasswords.confirmPassword ? "Hide confirm password" : "Show confirm password"}
                                title={visiblePasswords.confirmPassword ? "Hide confirm password" : "Show confirm password"}
                            >
                                <i className={`fas fa-eye${visiblePasswords.confirmPassword ? '-slash' : ''}`} />
                            </button>
                    </div>
                </div>

                <button className="auth-submit" type="submit" disabled={isLoading || Boolean(message)}>
                    {isLoading ? (
                        <><i className="fas fa-circle-notch fa-spin" /> Updating password...</>
                    ) : (
                        <>Update password <i className="fas fa-arrow-right" /></>
                    )}
                </button>
            </form>
            <div className="auth-form-footer">
                <Link className="auth-inline-link" to="/login">
                    <i className="fas fa-arrow-left" aria-hidden="true" /> Back to sign in
                </Link>
            </div>
        </AuthShell>
    );
};

export default ResetPassword;
